import { PerformanceReviewEvidenceHighlight } from '../types/v2';

const text = (value: unknown, limit = 2000) => String(value || '').trim().slice(0, limit);

export function confirmedExternalEvidenceHighlight(preparation: any): PerformanceReviewEvidenceHighlight | null {
  const sources = Array.isArray(preparation?.evidence)
    ? preparation.evidence.filter((entry: any) => entry?.scope === 'review' && Array.isArray(entry.records) && entry.records.length)
    : [];
  const entry = sources[sources.length - 1];
  if (!entry) return null;
  const primary = entry.records.find((record: any) => /furthest explicitly completed|last completed|teacher-provided evidence/i.test(text(record?.title))) || entry.records[0];
  const supporting = entry.records.filter((record: any) => record !== primary).slice(0, 4)
    .map((record: any) => `${text(record?.title, 200)}: ${text(record?.value, 500)}`).filter(Boolean).join('\n');
  return {
    source: text(entry.source, 200) || 'Confirmed external evidence',
    date: text(entry.date, 20),
    title: text(primary?.value, 500) || text(primary?.title, 500) || 'Confirmed external evidence',
    detail: supporting,
    verification: 'Teacher-confirmed for this review',
  };
}

export function latestKnownEvidence(context: Record<string, any>): PerformanceReviewEvidenceHighlight | null {
  const candidates: Array<PerformanceReviewEvidenceHighlight & { order: string }> = [];
  for (const item of context?.work_evidence || []) {
    if (item?.source_kind === 'primary_goal') continue;
    const title = text(item?.title, 500);
    const detail = text(item?.description || item?.rationale || item?.verification_note, 2000);
    if (!title && !detail) continue;
    candidates.push({
      source: text(item?.source_kind, 100).replace(/_/g, ' ') || 'Saved evidence',
      date: text(item?.date, 20), title: title || 'Saved evidence', detail,
      verification: text(item?.verification_status, 100).replace(/_/g, ' '),
      order: text(item?.date || item?.reviewed_at, 40),
    });
  }
  for (const item of context?.progress_summary?.work || []) {
    if (!item?.has_saved_progress && !item?.assessment_completed && item?.status !== 'completed') continue;
    candidates.push({
      source: text(item?.type, 100).replace(/_/g, ' ') || 'Class work', date: text(item?.date, 20),
      title: text(item?.title, 500) || 'Saved class work', detail: text(item?.status_label, 1000),
      verification: item?.assessment_completed ? 'Completed assessment record' : text(item?.status_label, 100),
      order: text(item?.date, 40),
    });
  }
  for (const item of context?.progress_summary?.daily_work || []) {
    if (!item?.wrap_up_submitted || !text(item?.wrap_up)) continue;
    candidates.push({
      source: item?.track === 'project' ? 'Project wrap-up' : 'Pathway wrap-up', date: text(item?.date, 20),
      title: text(item?.plan, 500) || 'Submitted daily wrap-up', detail: text(item?.wrap_up, 2000),
      verification: 'Student-submitted wrap-up', order: text(item?.date, 40),
    });
  }
  candidates.sort((a, b) => a.order.localeCompare(b.order));
  const latest = candidates[candidates.length - 1];
  if (!latest) return null;
  const { order: _order, ...highlight } = latest;
  return highlight;
}
